import { FC, Fragment, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router-dom";
import { PanelType as OfficePanelType } from "@fluentui/react";
import { LoadingOverlay, useTheme } from "cayo.ui";
import AlertItem from "@cayo/core/components/Alerts/AlertItem";
import { AlertType } from "@cayo/shared/components/Alerts/alertDispatcher";
import {
  getAlertsPanelStyles,
  getMenuButtonStyles,
} from "@cayo/shared/components/Alerts/Alerts.styles";
import hightlightedAlerts from "@cayo/shared/components/Alerts/hightlighted-alerts";
import { useAlerts } from "@cayo/shared/components/Alerts/hooks/useAlerts";
import { IAlertItem, AlertPanelProps } from "@cayo/shared/components/Alerts/types";
import alertUtils from "@cayo/shared/components/Alerts/utils";
import Badge from "@cayo/shared/components/Badge";
import ActionButton from "@cayo/shared/components/buttons/ActionButton";
import { animationEffects } from "@cayo/shared/components/common/Effects";
import { CenteredPanel, Column } from "@cayo/shared/components/CommonLayout";
import { useContextPanel } from "@cayo/shared/hooks/useContextPanel";
import {
  BadgeWrapper,
  BoldText,
  CloseIcon,
  ForwardIcon,
  LargeLink,
  RightPanel,
  Row1,
  Row2,
} from "./styles";

const AlertPanel: FC<AlertPanelProps> = (props) => {
  const panelType = alertUtils.getPanelType(props.name!);
  const { icon, viewAllAlertsUrl, onItemClick } = props;

  const {
    alertItems,
    isLoading,
    badgePos: badgeProps,
    badgeColor,
    rightPanelType,
    showRightPanel,
    setClickedItem,
    dismissAlert,
    clickedAlertRef,
  } = useAlerts(panelType as AlertType, props);

  const { hideContextPanel, contextPanelVisible } = useContextPanel();
  const theme = useTheme();
  const history = useHistory();

  useEffect(() => {
    if (contextPanelVisible) {
      showRightPanel(undefined);
    }
  }, [contextPanelVisible]);

  return (
    <>
      <ActionButton
        id={panelType}
        text=""
        type={"button"}
        onClick={() => {
          hideContextPanel();
          rightPanelType === panelType ? showRightPanel(undefined) : showRightPanel(panelType);
        }}
        styles={getMenuButtonStyles(rightPanelType === panelType, theme)}
        icon={{ iconName: icon }}
      />
      {alertItems && alertItems.length > 0 && (
        <BadgeWrapper>
          <Badge
            fontSize={10}
            color={badgeColor}
            {...badgeProps}
            width={8}
            height={8}
            top={23}
            right={17}
          />
        </BadgeWrapper>
      )}

      {rightPanelType === panelType && (
        <RightPanel
          styles={getAlertsPanelStyles(theme)}
          onRenderHeader={() => <Fragment />}
          onRenderNavigation={() => (
            <Column>
              <Row1>
                <BoldText variant={"xLarge"}>{props.title}</BoldText>
                <CloseIcon
                  iconProps={{ iconName: "cancel" }}
                  onClick={() => showRightPanel(undefined)}
                />
              </Row1>
              {(viewAllAlertsUrl || props.dismissAllAlertsUrl) && (
                <Row2>
                  {!!viewAllAlertsUrl && (
                    <LargeLink
                      variant={"xLarge"}
                      onClick={() => {
                        history.push(viewAllAlertsUrl);
                        showRightPanel(undefined);
                      }}
                    >
                      <FormattedMessage id="alerts.view-all" defaultMessage="View all alerts" />
                      <ForwardIcon iconName={"Forward"} />
                    </LargeLink>
                  )}
                </Row2>
              )}
            </Column>
          )}
          isBlocking={false}
          type={OfficePanelType.custom}
          customWidth={"400px"}
          onDismissed={() => showRightPanel(undefined)}
          isOpen={true}
        >
          {isLoading && <LoadingOverlay />}
          {!alertItems!.length && <CenteredPanel>No alerts found</CenteredPanel>}
          {alertItems!.length > 0 &&
            alertItems!
              .map((a) => a as IAlertItem)
              .map((a, i) => {
                const isClicked = hightlightedAlerts.isClicked(a);
                let c = (
                  <AlertItem
                    {...a}
                    key={a.id}
                    index={i}
                    showDetails={() => (onItemClick ? onItemClick(a) : setClickedItem(a))}
                    onDismiss={props.dismissAlertsUrl ? () => dismissAlert(a) : null}
                  />
                );
                if (isClicked) {
                  c = (
                    <animationEffects.pulse key={a.id} ref={isClicked ? clickedAlertRef : null}>
                      {c}
                    </animationEffects.pulse>
                  );
                }

                return c;
              })}
        </RightPanel>
      )}
    </>
  );
};

export default AlertPanel;
